import React, { useCallback, useState } from 'react';
import AdminUsageHistoryTab from '../AdminUsageHistoryTab';
import UsageByModelPanel from './UsageByModelPanel';
import UsageReconciliationPanel from './UsageReconciliationPanel';
import './UsageBilling.css';

/**
 * Раздел Usage & Billing в админке.
 *
 * Три среза одних и тех же денег, каждый отвечает на свой вопрос:
 *  · History — на что ушло, по функциональности и конкретной сущности;
 *  · By model — какая модель это съела (AIT-258);
 *  · Reconciliation — сходится ли счёт с тем, что насчитал ML (AIT-259).
 *
 * Шапка одна на весь раздел: заголовок, пояснение и обновление. Раньше каждый
 * срез рисовал собственную шапку со своей кнопкой Refresh, и на странице их
 * оказывалось несколько — здесь кнопка дёргает тот срез, который открыт.
 */

type UsageView = 'history' | 'by-model' | 'reconciliation';

const VIEWS: { key: UsageView; label: string; icon: string; hint: string }[] = [
  {
    key: 'history',
    label: 'History',
    icon: 'fa-list-ul',
    hint: 'Spend broken down by functionality and entity',
  },
  {
    key: 'by-model',
    label: 'By model',
    icon: 'fa-microchip',
    hint: 'Spend per user and model over a period',
  },
  {
    key: 'reconciliation',
    label: 'Reconciliation',
    icon: 'fa-scale-balanced',
    hint: 'ML ledger checked against the database',
  },
];

const SUBTITLE: Record<UsageView, string> = {
  history:
    'Every billed run across all users, grouped by what produced it. Open a row to see the operations behind a single run.',
  'by-model':
    'Token spend per user and model. This is the only place with a model breakdown — user-facing stats are reported without one.',
  reconciliation:
    'Comparison of ML-side totals against usage_records. Shadow rows are excluded from every other report and compared only here.',
};

const AdminUsageSection: React.FC<{ onOpenAdminTour?: () => void }> = ({ onOpenAdminTour }) => {
  const [view, setView] = useState<UsageView>('history');
  // Счётчик, а не колбэк: срез перезагружается по изменению значения, поэтому
  // шапке не нужно знать, как устроен активный срез и жив ли он ещё.
  const [refreshToken, setRefreshToken] = useState(0);

  const refresh = useCallback(() => setRefreshToken((n) => n + 1), []);

  return (
    <div className="ub-page" id="admin-billing-root">
      <header className="ub-page__head">
        <div className="ub-page__titles">
          <h2 className="ub-page__title">
            <i className="fas fa-wallet" aria-hidden="true" />
            Usage &amp; Billing
          </h2>
          <p className="ub-page__subtitle">{SUBTITLE[view]}</p>
        </div>
        <div className="ub-page__actions">
          {onOpenAdminTour ? (
            <button type="button" className="ub-btn" onClick={onOpenAdminTour} title="Guided tour for this section">
              <i className="fas fa-circle-question" aria-hidden="true" />
              Help
            </button>
          ) : null}
          <button type="button" className="ub-btn" onClick={refresh} title="Reload the current view">
            <i className="fas fa-rotate" aria-hidden="true" />
            Refresh
          </button>
        </div>
      </header>

      <nav className="ub-views" role="tablist" aria-label="Usage views">
        {VIEWS.map((item) => {
          const active = view === item.key;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={active}
              className={`ub-view${active ? ' ub-view--on' : ''}`}
              onClick={() => setView(item.key)}
              title={item.hint}
            >
              <i className={`fas ${item.icon}`} aria-hidden="true" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Срез монтируется только когда открыт: грузить три ручки разом незачем,
          а сверка вдобавок ходит в отдельный суточный отчёт. */}
      {view === 'history' ? <AdminUsageHistoryTab refreshToken={refreshToken} /> : null}
      {view === 'by-model' ? <UsageByModelPanel refreshToken={refreshToken} /> : null}
      {view === 'reconciliation' ? <UsageReconciliationPanel refreshToken={refreshToken} /> : null}
    </div>
  );
};

export default AdminUsageSection;
